import React, { useRef, useState, useEffect } from "react";
import { HeartIcon, ShareIcon, FlagIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Sample givers data
  const givers = [
    { name: "John", amount: 50, fund: "Community Help Fund" },
    { name: "Sarah", amount: 25, fund: "Prayer Support Fund" },
    { name: "Michael", amount: 100, fund: "Relief Campaign" },
  ];

  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // API base URL and default image
  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  // Function to handle card/image click
  const handleCardClick = (campaignSlug) => {
    navigate(`/campaign/${campaignSlug}`);
  };

  // Function to handle donate button click
  const handleDonateClick = (campaignSlug, e) => {
    e.stopPropagation();
    navigate(`/campaign/${campaignSlug}/donate`);
  };

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.donatepe.org/public/fundraisers');
        const data = await response.json();
        
        console.log("API Response:", data); // Debug log
        
        if (data.success && data.fundraisers) {
          let apiCampaigns = [...data.fundraisers];
          
          // Debug: Check what slug values we're getting
          console.log("Raw fundraisers data:", apiCampaigns.map(f => ({
            id: f.id,
            slug: f.slug,
            title: f.title
          })));
          
          const transformedCampaigns = apiCampaigns.map((fundraiser, index) => {
            const progress = fundraiser.goal_amount > 0 
              ? Math.min(100, Math.round((fundraiser.collected_amount / fundraiser.goal_amount) * 100))
              : 0;
            
            const colors = ["blue", "purple", "green"];
            const imageColor = colors[index % colors.length];
            
            const getCategory = () => {
              const title = fundraiser.title.toLowerCase();
              if (title.includes('medical') || title.includes('cure') || title.includes('recovery') || title.includes('accident')) 
                return "Medical";
              if (title.includes('church') || title.includes('prayer')) 
                return "Church";
              if (title.includes('mission') || title.includes('evangelism')) 
                return "Missions";
              if (title.includes('education')) 
                return "Education";
              if (title.includes('relief') || title.includes('flood')) 
                return "Relief";
              return "General";
            };

            let daysLeft = fundraiser.days_left;
            if (daysLeft === null || daysLeft === undefined) {
              daysLeft = Math.floor(Math.random() * 53) + 7;
            }

            const supporters = Math.floor(Math.random() * 3000) + 100;

            let imageUrl;
            if (fundraiser.cover_image) {
              if (fundraiser.cover_image.startsWith('/uploads/')) {
                imageUrl = `${API_BASE_URL}${fundraiser.cover_image}`;
              } else {
                imageUrl = fundraiser.cover_image;
              }
            } else {
              imageUrl = defaultCampaignImage;
            }

            // Ensure slug exists, if not create it
            const slug = fundraiser.slug || `campaign-${fundraiser.id}`;
            
            console.log(`Processing campaign ${fundraiser.id}:`, {
              originalSlug: fundraiser.slug,
              finalSlug: slug
            });

            return {
              id: fundraiser.id,
              title: fundraiser.title,
              description: slug.replace(/-/g, ' ').replace(/\d+/g, '').trim(),
              category: getCategory(),
              raised: fundraiser.collected_amount,
              goal: fundraiser.goal_amount,
              supporters: supporters,
              daysLeft: daysLeft,
              imageUrl: imageUrl,
              imageColor: imageColor,
              isVerified: fundraiser.collected_amount > 0 || fundraiser.goal_amount > 50000,
              progress: progress,
              slug: slug, // Use the ensured slug
              creatorName: fundraiser.creator_name,
              createdAt: fundraiser.created_at,
              hasCoverImage: !!fundraiser.cover_image,
            };
          });
          
          console.log("Transformed campaigns:", transformedCampaigns.map(c => ({
            id: c.id,
            slug: c.slug,
            title: c.title
          })));
          
          setCampaigns(transformedCampaigns);
        } else {
          console.error("API response not successful:", data);
          setCampaigns([]);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const scrollLeft = () => {
    if (sliderRef.current && campaigns.length >= 3) {
      const container = sliderRef.current;
      const card = container.querySelector('.campaign-card');
      if (card) {
        const cardWidth = card.offsetWidth + 32;
        container.scrollBy({ left: -cardWidth, behavior: "smooth" });
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
      }
    }
  };

  const scrollRight = () => {
    if (sliderRef.current && campaigns.length >= 3) {
      const container = sliderRef.current;
      const card = container.querySelector('.campaign-card');
      if (card) {
        const cardWidth = card.offsetWidth + 32;
        container.scrollBy({ left: cardWidth, behavior: "smooth" });
        setActiveIndex((prev) =>
          prev < campaigns.length - 3 ? prev + 1 : prev
        );
      }
    }
  };

  const handleScroll = () => {
    if (sliderRef.current && campaigns.length >= 3) {
      const container = sliderRef.current;
      const card = container.querySelector('.campaign-card');
      if (card) {
        const cardWidth = card.offsetWidth + 32;
        const scrollLeft = container.scrollLeft;
        const newIndex = Math.round(scrollLeft / cardWidth);
        setActiveIndex(Math.min(newIndex, campaigns.length - 3));
      }
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider && campaigns.length >= 3) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, [campaigns]);

  const visibleDotsCount = campaigns.length > 3 ? campaigns.length - 2 : 1;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#eef5ff] font-sans">
      <div className="max-w-[1500px] mx-auto px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-black">
            Trending Campaigns
          </h2>
        </div>

        {campaigns.length < 3 && !loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Not enough campaigns to display. Need at least 3 campaigns.</p>
          </div>
        ) : (
          <div className="relative flex items-center max-w-[1300px] mx-auto px-10">
            <button
              onClick={scrollLeft}
              className="absolute left-0 z-50 w-12 h-12 rounded-full bg-white text-[#00B7B3] shadow-lg text-xl hover:bg-[#00B7B3] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={activeIndex === 0 || campaigns.length <= 3}
            >
              ❮
            </button>

            {loading ? (
              <div className="flex gap-6 w-full justify-center">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="campaign-card w-[380px] flex-shrink-0 bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                    <div className="h-40 bg-gray-200"></div>
                    <div className="p-5">
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                      <div className="h-2 bg-gray-200 rounded w-full mb-6"></div>
                      <div className="flex justify-between">
                        <div className="h-8 bg-gray-200 rounded w-20"></div>
                        <div className="h-8 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                ref={sliderRef}
                className="flex gap-6 overflow-x-auto scroll-smooth w-full justify-start scrollbar-hide"
                style={{ 
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <div className="min-w-[10px] flex-shrink-0"></div>
                
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="campaign-card w-[380px] flex-shrink-0 bg-white rounded-2xl shadow-lg overflow-hidden transition-transform hover:-translate-y-1 cursor-pointer"
                    onClick={() => {
                      console.log("Card clicked, navigating with slug:", campaign.slug);
                      handleCardClick(campaign.slug);
                    }}
                  >
                    <div 
                      className="relative h-40"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("Image clicked, navigating with slug:", campaign.slug);
                        handleCardClick(campaign.slug);
                      }}
                    >
                      <img
                        src={campaign.imageUrl}
                        alt={campaign.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = `w-full h-full ${
                            campaign.imageColor === "blue"
                              ? "bg-gradient-to-r from-blue-400 to-cyan-400"
                              : campaign.imageColor === "purple"
                              ? "bg-gradient-to-r from-purple-400 to-pink-400"
                              : "bg-gradient-to-r from-green-400 to-emerald-400"
                          }`;
                          e.target.parentElement.appendChild(fallbackDiv);
                        }}
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className="bg-white px-3 py-1 rounded-full text-xs font-bold">
                          {campaign.category}
                        </span>

                        {campaign.isVerified && (
                          <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full flex items-center gap-1 text-xs">
                            <FlagIcon className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-[#0a2540] line-clamp-1">
                        {campaign.title}
                      </h3>
                      <p className="text-gray-600 text-sm mt-2 mb-3 line-clamp-2 h-10">
                        {campaign.description}
                      </p>

                      <div>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>
                            ${campaign.raised.toLocaleString()} raised
                          </span>
                          <span>
                            ${campaign.goal.toLocaleString()} goal
                          </span>
                        </div>

                        <div className="w-full h-1.5 bg-gray-200 rounded-full my-2 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-400 to-green-500"
                            style={{ width: `${campaign.progress}%` }}
                          ></div>
                        </div>

                        <div className="flex justify-between text-xs text-gray-600">
                          <span>{campaign.supporters.toLocaleString()} supporters</span>
                          <span>{campaign.daysLeft} days left</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center mt-4">
                        <button 
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle save functionality
                          }}
                        >
                          <HeartIcon className="w-4 h-4 mr-1" /> Save
                        </button>

                        <button 
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle share functionality
                          }}
                        >
                          <ShareIcon className="w-4 h-4 mr-1" /> Share
                        </button>

                        <button 
                          onClick={(e) => {
                            console.log("Donate clicked, slug:", campaign.slug);
                            handleDonateClick(campaign.slug, e);
                          }}
                          className="bg-[#00B7B3] text-white px-4 py-1.5 rounded-full font-semibold hover:bg-[#009c99] text-sm"
                        >
                          Donate
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="min-w-[10px] flex-shrink-0"></div>
              </div>
            )}

            <button
              onClick={scrollRight}
              className="absolute right-0 z-50 w-12 h-12 rounded-full bg-white text-[#00B7B3] shadow-lg text-xl hover:bg-[#00B7B3] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={activeIndex >= campaigns.length - 3 || campaigns.length <= 3}
            >
              ❯
            </button>
          </div>
        )}

        {campaigns.length >= 3 && !loading && (
          <div className="text-center mt-6">
            {Array.from({ length: visibleDotsCount }).map((_, index) => (
              <span
                key={index}
                className={`inline-block w-2.5 h-2.5 mx-1 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "bg-[#00B7B3] scale-125"
                    : "bg-slate-300"
                }`}
              ></span>
            ))}
          </div>
        )}

        {campaigns.length > 0 && !loading && (
          <div className="text-center mt-10">
            <button 
              className="bg-[#00B7B3] text-white px-10 py-3 rounded-full font-bold hover:bg-[#009c99] transition-colors"
              onClick={() => navigate('/campaigns/all')}
            >
              See More Campaigns
            </button>
          </div>
        )}

        <div className="mt-8 flex bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-[#00B7B3] text-white px-4 py-3 font-bold">
            Our Givers
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="flex gap-16 px-5 py-3 animate-[scroll_18s_linear_infinite] hover:[animation-play-state:paused]">
              {[...givers, ...givers].map((giver, index) => (
                <div
                  key={index}
                  className="whitespace-nowrap text-sm"
                >
                  {giver.name} just gave USD ${giver.amount} to
                  <span className="text-[#00B7B3] font-semibold">
                    {" "}
                    {giver.fund}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default Campaigns;